import { FC, useCallback, useMemo } from 'react'
import { OnboardingPagePreset } from 'components/features/creditCard/creditCardOnboarding/OnboardingPagePreset/OnboardingPagePreset'
import styles from 'components/features/creditCard/creditCardOnboarding/SSNStepPage/SSNStepPage.styl'
import { Field, isSubmitting, reduxForm, submit } from 'redux-form'
import { ReduxFormProps } from 'interfaces/component'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import { asyncValidator } from 'helpers/schemaHelpers'
import { shouldAsyncValidate } from 'helpers/formHelpers'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { PatchCCOnboardingSSNAction } from 'components/features/creditCard/creditCardStore/actions'
import { ssnFormSchema, CC_ONBOARDING_FORM_SSN } from 'components/features/creditCard/creditCardOnboarding/constants'
import Button from 'components/features/pfm/Button/Button'
import { Link } from 'react-router-dom'
import { useNextStep } from 'components/features/creditCard/creditCardOnboarding/useNextStep'
import { FormCommonError } from 'components/features/creditCard/creditCardOnboarding/FormCommonError/FormCommonError'
import { pushActionCreator } from 'actions/navigationActions'
import { useStepNumber } from 'components/features/creditCard/creditCardOnboarding/useStepNumber'


const SSN_PROPS = {
  placeholder: '123456789',
  qaMarker: 'ssn-input',
  autoComplete: 'new-password',
  maxLength: 9,
  type: 'password',
  pattern: '[0-9]*',
  hintIcon: 'secure',
  theme: 'snow',
}

type FormValues = {
  ssn: string
  ssn2: string
}

const EMPTY_OBJ = {}
export const SSNStepPage: FC = () => {
  const nextStep = useNextStep()

  const dispatch = useStoreDispatch()
  const handleSubmit = useCallback<(values: FormValues) => {}>((values) => {

    const { ssn } = values

    const person_data = {
      ssn: ssn.replace(/\D+/g, ''),
    }

    dispatch(new PatchCCOnboardingSSNAction({
      person_data,
      step: nextStep || 'review',
    }))

    return EMPTY_OBJ
  }, [dispatch, nextStep])

  const handleNextButton = useCallback(() => {
    dispatch(submit(CC_ONBOARDING_FORM_SSN))
  }, [dispatch])

  const formChild = useMemo(() => (
    <>
      <FormWrapped onSubmit={ handleSubmit } />
      <div className={ styles.poNote } data-qa-marker='form-note'>
        The SSN/ITIN you provide here will be submitted to an identity verification service and will not be permanently stored.
      </div>
    </>
  ), [handleSubmit])

  const isLoading = useStoreSelector((state) => isSubmitting(CC_ONBOARDING_FORM_SSN)(state))

  const handleClose = useCallback(() => {
    if (nextStep) {
      dispatch(pushActionCreator(`/credit-card/onboarding/${ nextStep }`))
    } else {
      dispatch(pushActionCreator(`/credit-card/onboarding/phone`))
    }
  }, [dispatch, nextStep])

  const stepNo = useStepNumber('ssn')
  return (
    <OnboardingPagePreset
      step={ stepNo }
      title='Enter your social security number'
      card={ formChild }
      onBack={ handleClose }
      progressPercent={ 50 }
    >

      <Button
        text='Next'
        onClick={ handleNextButton }
        fullWidth
        qaMarker='next-button'
        isSubmitting={ isLoading }
      />
      <Link to='/credit-card'>
        <Button
          text='Finish later'
          style='textOnly'
          fullWidth
          qaMarker='finish-later'
        />
      </Link>
    </OnboardingPagePreset>
  )
}

type FormProps =
  & ReduxFormProps<FormValues>
  & {}

const Form: FC<FormProps> = (props) => {
  const { handleSubmit, error } = props

  return (
    <form onSubmit={ handleSubmit }>
      <ul className={ styles.controlList }>
        <li className={ styles.controlListItem } data-qa-marker='ssn'>
          <Field
            name='ssn'
            component={ TextFieldWrapped }
            normalize={ (value: string) => value.replace(/\D/g, '') }
            props={{
              ...SSN_PROPS,
              label: 'Social Security Number / ITIN',

            }}
          />
        </li>
        <li className={ styles.controlListItem } data-qa-marker='confirm-ssn'>
          <Field
            name='ssn2'
            component={ TextFieldWrapped }
            normalize={ (value: string) => value.replace(/\D/g, '') }
            props={{
              ...SSN_PROPS,
              label: 'Confirm SSN/ITIN',
            }}
          />
        </li>
        <FormCommonError error={ error } className={ styles.controlListItem } />
      </ul>
    </form>
  )
}

const FormWrapped = reduxForm<FormValues, FormProps>({
  form: CC_ONBOARDING_FORM_SSN,
  asyncValidate: asyncValidator(ssnFormSchema),
  shouldAsyncValidate,
})(Form)
